/**
 * Types and enums for site change functionality
 * Provides type-safe interfaces for site selection and change operations
 */

/**
 * Enum defining the different sources/contexts for site changes
 * Used for analytics tracking and conditional business logic
 */
export enum SiteChangeSource {
    UserSelection = 'user-selection',
    LocationBased = 'location-based',
    Preselected = 'preselected',
    BookingDetails = 'booking-details',
    VenueWidget = 'venue-widget'
}

/**
 * Configuration options for site change operations
 * Allows customization of behavior based on the change context
 */
export interface SiteChangeOptions {
    /** The source/context of the site change */
    source: SiteChangeSource;
    
    /** Whether to clear existing booking data (default: true) */
    clearBooking?: boolean;
    
    /** Whether to clear existing gift voucher data (default: true) */
    clearVoucher?: boolean;
    
    /** Whether to load open dates for the new site (default: true) */
    loadOpenDates?: boolean;
    
    /** Whether to track analytics events (default: true) */
    trackAnalytics?: boolean;
    
    /** Whether to handle navigation/routing (default: true) */
    handleNavigation?: boolean;
    
    /** Whether to skip clearing applied discount codes (default: false) */
    skipCodeClearing?: boolean;
    
    /** Whether to skip showing modal dialogs (default: false) */
    skipModalDialogs?: boolean;
}

/**
 * Predefined option sets for common site change scenarios
 * Provides sensible defaults while allowing customization
 */
export const SiteChangePresets: Record<SiteChangeSource, SiteChangeOptions> = {
    [SiteChangeSource.UserSelection]: {
        source: SiteChangeSource.UserSelection,
        clearBooking: true,
        clearVoucher: true,
        loadOpenDates: true,
        trackAnalytics: true,
        handleNavigation: true,
        skipCodeClearing: false,
        skipModalDialogs: false,
    },
    
    [SiteChangeSource.LocationBased]: {
        source: SiteChangeSource.LocationBased,
        clearBooking: true,
        clearVoucher: true,
        loadOpenDates: true,
        trackAnalytics: true,
        handleNavigation: true,
        skipCodeClearing: false,
        skipModalDialogs: true, // Auto-selection shouldn't show modals
    },
    
    [SiteChangeSource.BookingDetails]: {
        source: SiteChangeSource.BookingDetails,
        clearBooking: false, // Don't clear - we're restoring booking data
        clearVoucher: false,
        loadOpenDates: false,
        trackAnalytics: false,
        handleNavigation: false,
        skipCodeClearing: true,
        skipModalDialogs: true,
    },
    
    [SiteChangeSource.Preselected]: {
        source: SiteChangeSource.Preselected,
        clearBooking: true,
        clearVoucher: true,
        loadOpenDates: true,
        trackAnalytics: true,
        handleNavigation: true,
        skipCodeClearing: true, // URL might have promo codes
        skipModalDialogs: false,
    },
    
    [SiteChangeSource.VenueWidget]: {
        source: SiteChangeSource.VenueWidget,
        clearBooking: true,
        clearVoucher: true,
        loadOpenDates: true,
        trackAnalytics: true,
        handleNavigation: true,
        skipCodeClearing: false,
        skipModalDialogs: false,
    }
} as const;
